/**
 * Per-room media context — facade that exposes mic/speaker controls
 * scoped to a single LiveKit Room. Replaces the prior pattern of
 * calling `mediaManager.setMicrophoneEnabled(room, ...)` on the
 * singleton, which mutated process-wide state and broke when two
 * rooms were active concurrently.
 *
 * See `docs/superpowers/specs/2026-05-16-concurrent-calls-mediamanager-design.md`
 * for the full design rationale.
 *
 * Scope split (CTO decision 2026-05-16):
 *   - Microphone     → per-room via shared capture (this file)
 *   - Speakers       → per-room (mute remote `<audio>` elements scoped
 *                      to THIS room)
 *   - Camera / Screen → mutex across rooms (one owner at a time); the
 *                      `MediaManager` singleton retains those paths and
 *                      surfaces `CameraBusyError` to the caller when a
 *                      second context tries to take them. Not handled
 *                      here.
 */

import { LocalAudioTrack, type Room, type LocalTrackPublication, Track, RoomEvent } from 'livekit-client'
import { acquireMic, type MicHandle, type DenoiserStatus } from './sharedMicCapture'
import { computeWarmEligibility, isWarmMicEnabled, WARM_MIC_GRACE_MS, type RoomKind } from './warmMic'
import { resumeAndVerifyManagedAudioContexts } from '../components/voice/audioContext'

/**
 * How long the shared mic capture stays warm after a room is torn down
 * (destroy), so a room switch (leave room A → join room B on the same
 * mic) reuses the live getUserMedia + denoiser graph instead of paying
 * a fresh ~200-500 ms re-acquire. Applied ONLY on destroy — never on
 * mute, where invariant §28 requires the OS capture to end immediately
 * (a held capture trips Windows comms ducking of all other audio).
 * Kept short so the only §28 exposure (leaving a call while UNMUTED and
 * not rejoining) is bounded to a few seconds.
 */
const ROOM_SWITCH_GRACE_MS = 8_000

// TEMP probe (1.1.162A) — the caller stack of the most recent per-room mic
// DISABLE, surfaced to ClickHouse via voiceTelemetry. The channel⇄DM mute
// coupling is a direct setMicrophoneEnabled(room,false) whose caller is NOT
// visible in aggregate telemetry; this names it from ONE repro instead of
// guessing. Module-level (last write wins) — we query right after the repro.
// Remove once the coupling is root-caused.
let _lastMicDisable = ''
export function getLastMicDisable(): string { return _lastMicDisable }

export interface MicOptions {
  deviceId?: string | null
  /**
   * Override the user's noise-suppression preference. Most callers omit
   * this and let the user's persisted pref (localStorage) win.
   */
  noiseSuppression?: boolean
}

export interface MediaContextOptions {
  /**
   * Optional label used in logs / errors ("voice channel", "DM call").
   * Default falls back to `room.name`.
   */
  label?: string
  /**
   * Hook invoked at the very end of `destroy()`. mediaManager uses this
   * to clear camera/screen ownership and drop the WeakMap entry, so a
   * yielded resource can be reclaimed by another context immediately.
   */
  onDestroy?: () => void
  /**
   * Optional capture-isolation namespace for THIS room's mic. When set (e.g.
   * 'dm'), the room acquires a SEPARATE shared-mic capture keyed by it, so a
   * concurrent room on the same device/NS (the voice channel) can't tear down
   * this room's mic track on its own mute. See sharedMicCapture `isolationKey`.
   * Kill-switch: localStorage 'voiceDmCaptureIsolation'='false' falls back to
   * the shared capture.
   */
  captureIsolation?: string
  /**
   * Room kind for the warm-mic policy (see warmMic.ts). 'dm' is always
   * warm-eligible; 'channel' (default) is eligible when the camera is on OR
   * there are >=2 participants. Set 'dm' from dmCallStore.
   */
  kind?: RoomKind
  /**
   * Fired whenever this room's denoiser state changes (idle → agc-only →
   * rnnoise via the #58 stopgap swap, or any subsequent rebuild). Used by
   * mediaManager.forRoom to mirror the per-room state into its SINGLETON
   * `_denoiserState` so legacy listeners (VoiceChannelControls' mic warning
   * spinner via mediaManager.subscribeDenoiser) see the active backend.
   * Without this forward the singleton stays at its initial 'idle' for the
   * whole session and the mic button shows a perpetual amber "connecting"
   * spinner even though audio is actually flowing fine through the per-room
   * publish (Dmitriy 2026-05-26 on 1.1.172A).
   */
  onDenoiserChange?: (state: DenoiserStatus) => void
}

/**
 * One per active Room. Stores hold their own MediaContext alongside
 * their Room reference and call `setMicrophoneEnabled`, `setMuted`,
 * etc. through it. On disconnect, `destroy()` releases the mic handle
 * and tears down listeners.
 */
export class MediaContext {
  readonly room: Room
  readonly label: string

  private onDestroyHook: (() => void) | undefined
  private onDenoiserChangeHook: ((state: DenoiserStatus) => void) | undefined
  private micHandle: MicHandle | null = null
  /**
   * Single-flight chain for mic ops within THIS room. Prevents the
   * webrtc-internals "≥7 parallel getUserMedia in 1 ms" race the old
   * mediaManager mitigated globally — now scoped per-room so a busy
   * voice channel doesn't block a DM call's toggle.
   */
  private micOpChain: Promise<boolean> = Promise.resolve(true)
  private currentDeviceId: string | null = null
  private destroyed = false
  /** True while the mic is held WARM (handle alive, publication muted). */
  private warmMuted = false
  /** Pending WARM→COLD grace timer (eligibility dropped while warm). */
  private warmGraceTimer: ReturnType<typeof setTimeout> | null = null
  /** Teardown for the warm-policy room-event listeners. */
  private unbindWarmPolicy: (() => void) | null = null
  /** Capture-isolation namespace for this room (e.g. 'dm'); see MediaContextOptions. */
  private readonly captureIsolation: string | undefined
  /** Room kind for the warm-mic policy (see warmMic.ts). Defaults to 'channel'. */
  private readonly kind: RoomKind

  /** Speakers are independently mutable per room (per remote `<audio>` element). */
  private speakersMuted = false

  /** Latest known denoiser state. Updated from the shared capture's subscription. */
  private denoiserState: DenoiserStatus = 'idle'
  private denoiserListeners = new Set<(state: DenoiserStatus) => void>()
  private unsubscribeFromCaptureDenoiser: (() => void) | null = null
  /**
   * Track-swap subscription (#58 — AGC stopgap → denoiser handoff). When the
   * background denoiser cascade replaces the interim AGC-only graph, the
   * shared capture fires this with a fresh clone — we `replaceTrack` on the
   * live LiveKit publication so peers seamlessly migrate from AGC-only audio
   * to denoised audio (no unpublish/republish, no SDP renegotiation).
   */
  private unsubscribeFromTrackSwap: (() => void) | null = null

  constructor(room: Room, opts: MediaContextOptions = {}) {
    this.room = room
    this.label = opts.label ?? room.name ?? '(unnamed room)'
    this.onDestroyHook = opts.onDestroy
    this.onDenoiserChangeHook = opts.onDenoiserChange
    this.captureIsolation = opts.captureIsolation
    this.kind = opts.kind ?? 'channel'
    this._bindWarmPolicyEvents()
  }

  /** Is THIS room currently a warm-eligible context? See warmMic.ts. */
  private warmEligible(): boolean {
    return computeWarmEligibility({
      kind: this.kind,
      cameraEnabled: !!this.room.localParticipant.isCameraEnabled,
      participantCount: this.room.remoteParticipants.size + 1,
    })
  }

  /**
   * Subscribe to the room events that change warm-eligibility (participant
   * join/leave, local camera publish/unpublish). Guarded for non-LiveKit test
   * fakes / SSR. Camera on/off ALSO drives a direct hook from
   * mediaManager.setCameraEnabled (_onWarmSignal) for reliability.
   */
  private _bindWarmPolicyEvents(): void {
    const room = this.room as unknown as {
      on?: (e: string, fn: () => void) => void
      off?: (e: string, fn: () => void) => void
    }
    if (typeof room.on !== 'function') return
    const reeval = () => this._reevaluateWarm()
    room.on(RoomEvent.ParticipantConnected, reeval)
    room.on(RoomEvent.ParticipantDisconnected, reeval)
    room.on(RoomEvent.LocalTrackPublished, reeval)
    room.on(RoomEvent.LocalTrackUnpublished, reeval)
    this.unbindWarmPolicy = () => {
      try {
        room.off?.(RoomEvent.ParticipantConnected, reeval)
        room.off?.(RoomEvent.ParticipantDisconnected, reeval)
        room.off?.(RoomEvent.LocalTrackPublished, reeval)
        room.off?.(RoomEvent.LocalTrackUnpublished, reeval)
      } catch { /* noop */ }
    }
  }

  /** Public hook so mediaManager can nudge a re-evaluation after a camera toggle. */
  _onWarmSignal(): void {
    this._reevaluateWarm()
  }

  /**
   * Re-evaluate warm-eligibility. When warm-muted and the context is no longer
   * eligible, schedule a cold teardown after the grace; when it becomes eligible
   * again, cancel the pending teardown.
   */
  private _reevaluateWarm(): void {
    if (this.destroyed || !isWarmMicEnabled()) return
    if (!this.warmMuted) return
    if (this.warmEligible()) this._cancelWarmGrace()
    else this._scheduleWarmGrace()
  }

  private _scheduleWarmGrace(): void {
    if (this.warmGraceTimer !== null) return
    // Deliberately do NOT reset an already-pending timer — the first ineligibility
    // starts the 30s clock; redundant ineligibility events must not extend it.
    this.warmGraceTimer = setTimeout(() => {
      this.warmGraceTimer = null
      if (this.destroyed || !this.warmMuted || this.warmEligible()) return
      void this._coldReleaseFromWarm()
    }, WARM_MIC_GRACE_MS)
  }

  private _cancelWarmGrace(): void {
    if (this.warmGraceTimer !== null) {
      clearTimeout(this.warmGraceTimer)
      this.warmGraceTimer = null
    }
  }

  /**
   * Tear a warm-muted mic down to COLD (release OS capture, stop ducking) while
   * the user stays muted. Serialised through the mic-op chain so it can't race a
   * user toggle.
   */
  private async _coldReleaseFromWarm(): Promise<void> {
    const next = this.micOpChain.then(async () => {
      if (this.destroyed || !this.warmMuted || this.warmEligible()) return false
      await this._coldReleaseMic()
      return false
    })
    this.micOpChain = next.catch(() => false)
    // Fire-and-forget (the grace timer voids the result): swallow here so a
    // _coldReleaseMic rejection can't surface as an unhandled promise rejection.
    await next.catch(() => {})
  }

  // ─────────────────────────────────────────────────────────────
  //  Microphone
  // ─────────────────────────────────────────────────────────────

  /**
   * Publish (enabled=true) or unpublish (enabled=false) the microphone
   * for THIS room only. Other concurrent rooms keep their publications
   * intact. The underlying hardware capture is reference-counted: the
   * first room to enable opens getUserMedia, the last room to disable
   * releases it.
   */
  async setMicrophoneEnabled(enabled: boolean, options?: MicOptions): Promise<boolean> {
    if (!enabled) {
      // TEMP probe (#mute-coupling): record WHO disabled THIS room's mic.
      try {
        _lastMicDisable = `${this.label}|${new Date().toISOString().slice(11, 23)}|` +
          ((new Error().stack || '').split('\n').slice(2, 8).map((s) => s.trim().replace(/^at\s+/, '')).join(' <- '))
      } catch { /* never let the probe affect the call */ }
    }
    const next = this.micOpChain.then(() => this._setMicrophoneEnabledInner(enabled, options))
    this.micOpChain = next.catch(() => false)
    return next
  }

  async toggleMicrophone(): Promise<boolean> {
    const currentlyEnabled = !!this.findMicPublication()
    return this.setMicrophoneEnabled(!currentlyEnabled)
  }

  async setAudioDevice(deviceId: string | null): Promise<boolean> {
    const wasEnabled = !!this.findMicPublication()
    return this.setMicrophoneEnabled(wasEnabled, { deviceId })
  }

  /**
   * Cold-release THIS room's mic: stop + unpublish every mic publication, sync
   * the LiveKit flag, release the shared handle. Used by the cold mute path and
   * (later) by the warm→cold grace teardown.
   *
   * Invariant §28 / #17: the track is stopped BEFORE awaiting unpublish so the
   * OS-level capture ends even if `unpublishTrack` rejects on a network blip —
   * otherwise Windows' communications policy keeps ducking every other audio
   * source by ~30% while we're "muted". `unpublishTrack(track, true)` also stops,
   * so the explicit `track.stop()` is a deliberate, idempotent belt-and-braces.
   */
  private async _coldReleaseMic(): Promise<void> {
    const participant = this.room.localParticipant
    const micPubs: LocalTrackPublication[] = Array.from(participant.trackPublications.values()).filter(
      (p): p is LocalTrackPublication =>
        p.kind === Track.Kind.Audio && p.source === Track.Source.Microphone,
    )
    for (const pub of micPubs) {
      const track = pub.track
      if (!track) continue
      try { track.stop() } catch { /* noop */ }
      try {
        await participant.unpublishTrack(track, /* stopOnUnpublish */ true)
      } catch (err) {
        console.warn(`[MediaContext:${this.label}] mic unpublish failed:`, (err as Error)?.message ?? err)
      }
    }
    try {
      await participant.setMicrophoneEnabled(false)
    } catch {
      // Post-unpublish this is sometimes a no-op; the flag syncs on next observation.
    }
    this.releaseMicHandle()
    this.warmMuted = false
  }

  private async _setMicrophoneEnabledInner(enabled: boolean, options?: MicOptions): Promise<boolean> {
    if (this.destroyed) return false

    if (!enabled) {
      // WARM mute: in a live context, keep the OS mic + denoiser graph warm and
      // just mute the publication (track.mute() → mediaStreamTrack.enabled=false
      // + SFU muted flag → Opus DTX, zero audio on the wire). Re-unmute is then
      // an instant track.unmute(). Gated by the kill-switch + eligibility.
      if (isWarmMicEnabled() && this.micHandle && this.warmEligible()) {
        const pub = this.findMicPublication()
        if (pub?.track instanceof LocalAudioTrack && this.micHandle.track.readyState === 'live') {
          try {
            await pub.track.mute()
            this.warmMuted = true
            return false
          } catch (err) {
            console.warn(`[MediaContext:${this.label}] warm mute failed; cold-releasing instead:`, err)
            // fall through to cold release
          }
        }
      }
      await this._coldReleaseMic()
      return false
    }

    // WARM unmute fast-path: a muted publication on a live, matching handle →
    // instant track.unmute(). No getUserMedia, no republish. Only when no
    // device/NS override is requested (a switch must take the cold re-acquire).
    if (
      isWarmMicEnabled() &&
      this.warmMuted &&
      this.micHandle?.track.readyState === 'live' &&
      options?.deviceId == null &&
      options?.noiseSuppression == null
    ) {
      const pub = this.findMicPublication()
      if (pub?.track instanceof LocalAudioTrack && pub.track.isMuted) {
        // Resume + VERIFY the denoiser graph's AudioContext is RUNNING before we
        // unmute the publication into it. On mobile/backgrounded tabs the
        // per-second health tick that keeps the graph alive (#53/#65) is
        // throttled, so a warm-muted graph can sit suspended; track.unmute()
        // would then publish a SILENT suspended graph — the warm-unmute
        // silent-publish bug ("микрофон не включается", mobile). The unmute is
        // normally a button tap, so a user gesture is in scope and resume()
        // succeeds. If the graph can't be confirmed live, release the stale
        // handle and fall through to a cold acquire+publish that REBUILDS the
        // denoiser graph on a fresh context — never unmute into silence.
        const ctxLive = await resumeAndVerifyManagedAudioContexts()
        if (ctxLive) {
          try {
            await pub.track.unmute()
            this.warmMuted = false
            this._cancelWarmGrace()
            return true
          } catch (err) {
            console.warn(`[MediaContext:${this.label}] warm unmute failed; cold-publishing instead:`, err)
            this.warmMuted = false
            // fall through to the cold acquire+publish path
          }
        } else {
          console.warn(`[MediaContext:${this.label}] warm unmute skipped: denoiser AudioContext suspended — cold-rebuilding to avoid a silent mic`)
          this.warmMuted = false
          // Force a true cold rebuild: the cold path below reuses this.micHandle
          // when the device matches, which would just republish the SAME
          // suspended graph. Drop it so acquireMic builds a fresh denoiser graph
          // on a resumable context.
          this.releaseMicHandle()
          // fall through to the cold acquire+publish path
        }
      }
    }
    // Committing to a cold (re)publish below → the resulting mic is HOT, not
    // warm-muted. Clear the flag so a later ineligibility can't let the grace
    // timer cold-release an actively-used hot mic (device-change re-acquire path).
    this.warmMuted = false

    // Enabling: acquire a (possibly shared) mic handle and publish a track for THIS room.
    const deviceId = options?.deviceId === null ? undefined : options?.deviceId ?? this.currentDeviceId ?? undefined
    const ns = typeof options?.noiseSuppression === 'boolean'
      ? options.noiseSuppression
      : (typeof window !== 'undefined' && localStorage.getItem('noiseSuppression') !== 'false')

    // If we already hold a handle and the device/NS combo hasn't changed, reuse it.
    // Otherwise release the existing and acquire a fresh one.
    const wantedDevice = deviceId ?? '__default__'
    const handleStillMatches = this.micHandle && this.currentDeviceId === (deviceId ?? null)
    if (!handleStillMatches) {
      this.releaseMicHandle()
    }

    if (!this.micHandle) {
      try {
        // DM-capture isolation (#mute-coupling fix): when this context carries a
        // captureIsolation key, acquire a SEPARATE shared capture (distinct key)
        // so a concurrent room (the voice channel) muting can't tear down THIS
        // room's mic track. Proven root cause: a channel mute unpublished the DM
        // mic with ZERO DM-side setMicrophoneEnabled calls (shared-capture teardown).
        // Kill-switch: localStorage 'voiceDmCaptureIsolation'='false' → shared (old).
        const isoOn = typeof window === 'undefined' || localStorage.getItem('voiceDmCaptureIsolation') !== 'false'
        const isolationKey = isoOn ? this.captureIsolation : undefined
        this.micHandle = await acquireMic({ deviceId, noiseSuppression: ns, isolationKey })
        this.currentDeviceId = deviceId ?? null
        // Subscribe so denoiser state changes (cascade fallback after capture-build) propagate.
        this.unsubscribeFromCaptureDenoiser?.()
        this.unsubscribeFromCaptureDenoiser = this.micHandle.subscribeDenoiser((state) => {
          this.denoiserState = state
          for (const fn of this.denoiserListeners) {
            try { fn(state) } catch { /* noop */ }
          }
          // Mirror per-room state into mediaManager's SINGLETON `_denoiserState`
          // so legacy UI subscribers (VoiceChannelControls amber-spinner gate,
          // DenoiserBadge, etc.) reading via `mediaManager.subscribeDenoiser`
          // see the active backend. Without this forward the singleton stays
          // at initial 'idle' for the whole per-room session — exactly what
          // produced the perpetual amber mic spinner on 1.1.172A while audio
          // was actually flowing fine.
          try { this.onDenoiserChangeHook?.(state) } catch { /* never let UI forward kill the call */ }
        })
        // NOTE: subscribeTrackSwap MUST be called AFTER publishTrack completes
        // — see the post-publish block below. Subscribing here races with the
        // background denoiser swap completing mid-publishTrack: if Case 2
        // (swap already done) fires, the queueMicrotask runs while findMicPublication()
        // still returns null → silent bail → AGC stopgap teardown destroys
        // the still-published clone → mic publishes silence forever ("крутится
        // подключение", Dmitriy 2026-05-26 on 1.1.171A). Defer the subscribe.
      } catch (err) {
        console.error(`[MediaContext:${this.label}] acquireMic failed:`, err)
        return false
      }
    }
    void wantedDevice

    // Unpublish any existing mic publication on this room before publishing the new
    // handle's track — otherwise livekit complains about duplicate sources.
    const existing = this.findMicPublication()
    if (existing?.track) {
      try {
        await this.room.localParticipant.unpublishTrack(existing.track, /* stopOnUnpublish */ false)
      } catch {
        // Best-effort cleanup of a stale publication; not fatal.
      }
    }

    try {
      const localTrack = new LocalAudioTrack(this.micHandle.track, undefined, /* userProvidedTrack */ true)
      await this.room.localParticipant.publishTrack(localTrack, { source: Track.Source.Microphone })
      // Track-swap subscription (#58) — DEFERRED until AFTER publishTrack so
      // findMicPublication() in _applyTrackSwap reliably finds the new pub.
      // If the background denoiser swap already completed while we were
      // awaiting publishTrack (Case 2), subscribeTrackSwap immediately queues
      // a microtask with the denoiser clone — by now the pub exists, so
      // _applyTrackSwap can replaceTrack cleanly. If the swap is still
      // pending (Case 1), the listener registers and fires later. Either
      // way the live publication is migrated from AGC-only → denoiser
      // without unpublish/republish (no SDP renegotiation, no audible glitch).
      this.unsubscribeFromTrackSwap?.()
      this.unsubscribeFromTrackSwap = this.micHandle.subscribeTrackSwap((newTrack) => {
        void this._applyTrackSwap(newTrack)
      })
      return true
    } catch (err) {
      console.error(`[MediaContext:${this.label}] publishTrack failed:`, err)
      this.releaseMicHandle()
      return false
    }
  }

  private findMicPublication(): LocalTrackPublication | null {
    for (const pub of this.room.localParticipant.trackPublications.values()) {
      if (pub.kind === Track.Kind.Audio && pub.source === Track.Source.Microphone) {
        return pub as LocalTrackPublication
      }
    }
    return null
  }

  private releaseMicHandle(graceMs = 0): void {
    if (this.micHandle) {
      try { this.micHandle.release({ graceMs }) } catch { /* noop */ }
      this.micHandle = null
    }
    if (this.unsubscribeFromCaptureDenoiser) {
      this.unsubscribeFromCaptureDenoiser()
      this.unsubscribeFromCaptureDenoiser = null
    }
    if (this.unsubscribeFromTrackSwap) {
      this.unsubscribeFromTrackSwap()
      this.unsubscribeFromTrackSwap = null
    }
    this.denoiserState = 'idle'
  }

  /**
   * Handle a track-swap notification from the shared capture (#58). The
   * background denoiser cascade finished — `newTrack` is a fresh clone of
   * the denoiser's processed output. We `replaceTrack` on the live LiveKit
   * publication so peers seamlessly migrate from AGC-only to denoised
   * audio. The OLD track (the AGC-only clone we initially published) is
   * stopped by LiveKit's replaceTrack contract.
   *
   * Best-effort: any failure here keeps the AGC-only audio flowing (still
   * leveled, just no NS) — never throws into a setMicrophoneEnabled caller.
   */
  private async _applyTrackSwap(newTrack: MediaStreamTrack): Promise<void> {
    if (this.destroyed) {
      try { newTrack.stop() } catch { /* noop */ }
      return
    }
    const pub = this.findMicPublication()
    const localTrack = pub?.track
    if (!localTrack || !(localTrack instanceof LocalAudioTrack)) {
      // No live publication to swap (mid-toggle race, or mic was disabled
      // between buildCapture and swap completion). Stop the dangling clone.
      try { newTrack.stop() } catch { /* noop */ }
      return
    }
    try {
      await localTrack.replaceTrack(newTrack)
      console.info(`[MediaContext:${this.label}] AGC stopgap → denoiser track-swap applied (replaceTrack).`)
      // OSPROD-137: replaceTrack fires NO LocalTrackPublished and leaves
      // pub.track.mediaStreamTrack pointing at the now-`ended` old track
      // (VOICE_CRITICAL_INVARIANTS #1/#2). The live-transcription recorder
      // (useTranscription) clones the published mic track; after this swap
      // its clone is dead and it cannot re-acquire via the stale getter →
      // the live transcript silently stops until a full republish (mic
      // toggle / reload). Hand the FRESH track straight to the transcription
      // layer so it re-arms its MediaRecorder on it. Best-effort + kill-
      // switched (`voiceTranscriptSwapResync`='false' disables); never let
      // it throw into the swap, which is itself best-effort.
      try {
        const resyncOn =
          typeof localStorage === 'undefined' || localStorage.getItem('voiceTranscriptSwapResync') !== 'false'
        if (resyncOn && typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('voice:micTrackSwapped', { detail: { room: this.room, track: newTrack } }),
          )
        }
      } catch { /* noop — diagnostics must never break the swap */ }
    } catch (err) {
      console.warn(`[MediaContext:${this.label}] track-swap replaceTrack failed; staying on AGC-only:`, err)
      try { newTrack.stop() } catch { /* noop */ }
    }
  }

  // ─────────────────────────────────────────────────────────────
  //  Speakers — incoming audio for THIS room
  // ─────────────────────────────────────────────────────────────

  /**
   * Mute or unmute incoming audio from every remote participant in
   * THIS room. The other room's incoming audio is untouched.
   */
  setSpeakersMuted(muted: boolean): void {
    this.speakersMuted = muted
    for (const participant of this.room.remoteParticipants.values()) {
      for (const pub of participant.trackPublications.values()) {
        if (pub.kind !== Track.Kind.Audio) continue
        const track = pub.track
        if (!track) continue
        try {
          // livekit's RemoteAudioTrack attaches a hidden <audio> element by
          // default; mute that element. Both `setMuted(true)` and
          // `attachedElements[i].muted = true` work — go with the explicit
          // element route so it's obvious in DevTools.
          const els = ((track as any).attachedElements ?? []) as HTMLMediaElement[]
          for (const el of els) {
            try { (el as HTMLAudioElement).muted = muted } catch { /* noop */ }
          }
        } catch (err) {
          console.warn(`[MediaContext:${this.label}] speaker mute on remote failed:`, err)
        }
      }
    }
  }

  isSpeakersMuted(): boolean {
    return this.speakersMuted
  }

  // ─────────────────────────────────────────────────────────────
  //  Denoiser status
  // ─────────────────────────────────────────────────────────────

  getDenoiserState(): DenoiserStatus {
    return this.denoiserState
  }

  /**
   * The live published mic track for THIS room — the sharedMicCapture clone,
   * which IS the denoiser output when NS is on — or null when the mic isn't
   * published. Read by the singleton health-check's denoiser-integrity probe so
   * it can verify the PER-ROOM denoiser instead of the singleton handle (which
   * is null for per-room publishes → it stormed a bogus reapply every tick).
   * See mediaManager.startHealthCheck + VOICE_CRITICAL_INVARIANTS #52.
   */
  getPublishedMicTrack(): MediaStreamTrack | null {
    return this.micHandle?.track ?? null
  }

  subscribeDenoiser(fn: (state: DenoiserStatus) => void): () => void {
    this.denoiserListeners.add(fn)
    fn(this.denoiserState)
    return () => { this.denoiserListeners.delete(fn) }
  }

  // ─────────────────────────────────────────────────────────────
  //  Lifecycle
  // ─────────────────────────────────────────────────────────────

  /**
   * Tear down this context. Releases the mic handle (decrements the
   * shared capture's refCount), clears subscribers. Called from
   * `livekitStore.disconnect` / `dmCallStore.disconnect`.
   */
  async destroy(): Promise<void> {
    if (this.destroyed) return
    this.destroyed = true
    // Call-end teardown: release the shared mic capture IMMEDIATELY (grace 0)
    // so the NEXT call rebuilds the denoiser through applyRNNoise's
    // ctx.state==='running' guard (invariant #16) instead of warm-reusing a
    // graph whose singleton RNNoise AudioContext may have suspended to SILENCE
    // while idle (invariant #10 — encoded silence at full Opus bitrate,
    // denoiser still reports 'rnnoise', local_audio_level=0).
    //
    // The 8s warm-keep (ROOM_SWITCH_GRACE_MS) caused the DM "2nd call send is
    // dead until a full page reload" blocker: a DM call placed within 8s of
    // hangup took acquireMic's warm-reuse path (clone publishSourceTrack with
    // NO applyRNNoise) → bypassed the ctx guard → published a silent track.
    // It only ever helped a full-hangup→rejoin-within-8s (the room-SWITCH path
    // disconnects the old Room directly and never calls destroy(), so the grace
    // never served its stated purpose). The channel's mute→unmute already uses
    // grace 0 (releaseMicHandle() default) — the proven-working rebuild path we
    // now match. Grace 0 also releases the OS mic promptly on leave (better for
    // the Windows comms-ducking invariant #17, not worse).
    // Kill-switch: localStorage 'voiceCaptureGraceOnLeave'='true' restores 8s.
    this._cancelWarmGrace()
    this.unbindWarmPolicy?.()
    this.unbindWarmPolicy = null
    this.warmMuted = false
    const graceOnLeave =
      typeof window !== 'undefined' && localStorage.getItem('voiceCaptureGraceOnLeave') === 'true'
    this.releaseMicHandle(graceOnLeave ? ROOM_SWITCH_GRACE_MS : 0)
    this.denoiserListeners.clear()
    try { this.onDestroyHook?.() } catch { /* never let cleanup throw */ }
  }
}
